import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  EmailMessage,
  EmailMessageStatus,
  EmailTemplate,
} from "@sales-ops-amplify/shared";
import {
  bulkDeleteEmails,
  bulkRescheduleEmails,
  bulkRetryEmails,
  createEmailDraft,
  deleteEmail,
  getEmailStats,
  getEmailThread,
  listEmails,
  processScheduledEmails,
  replyEmail,
  sendEmail,
  syncEmails,
  updateEmailDraft,
} from "@/api/emails";
import { listEmailTemplates, listEmailSnippets } from "@/api/email-templates";
import { listMailboxes } from "@/api/mailboxes";
import { useDebouncedValue } from "@/hooks/usePagination";
import { listSequences } from "@/api/sequences";
import { listWorkspaceLists } from "@/api/lists";
import { cn } from "@/cn";

const STATUS_TABS: Array<{ id: EmailMessageStatus | ""; label: string }> = [
  { id: "", label: "All" },
  { id: "draft", label: "Drafted" },
  { id: "scheduled", label: "Scheduled" },
  { id: "sent", label: "Delivered" },
  { id: "replied", label: "Replied" },
  { id: "failed", label: "Failed" },
  { id: "bounced", label: "Bounced" },
];

function formatWhen(iso?: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function stripHtml(html?: string): string {
  if (!html) return "";
  return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function statusLabel(status: EmailMessageStatus): string {
  if (status === "sent") return "Delivered";
  if (status === "draft") return "Drafted";
  return status.replace("_", " ");
}

export function EmailsLayout() {
  return (
    <div className="space-y-5 pb-10">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Emails</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Holistic overview of outreach across contacts and sequences —
            delivery status, opens, replies, and follow-ups.
          </p>
        </div>
      </div>
      <nav className="flex gap-1 border-b border-border">
        {[
          { to: "/emails", label: "Mailbox", end: true },
          { to: "/emails/templates", label: "Templates" },
        ].map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            className={({ isActive }) =>
              cn(
                "px-3 py-2 text-sm",
                isActive
                  ? "border-b-2 border-brand font-medium text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )
            }
          >
            {t.label}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </div>
  );
}

export function EmailsHubPage() {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(true);
  const [status, setStatus] = useState<EmailMessageStatus | "">("");
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q.trim(), 300);
  const [mailboxId, setMailboxId] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [sequenceId, setSequenceId] = useState("");
  const [listId, setListId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [notSentReason, setNotSentReason] = useState("");
  const [opened, setOpened] = useState<"" | "true" | "false">("");
  const [minOpens, setMinOpens] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [composeOpen, setComposeOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [scheduleAt, setScheduleAt] = useState("");
  const [composeMailboxId, setComposeMailboxId] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [replyScheduleAt, setReplyScheduleAt] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  const [rowMenuId, setRowMenuId] = useState<string | null>(null);

  useEffect(() => {
    const composeTo = searchParams.get("composeTo");
    if (!composeTo) return;
    setTo(composeTo);
    setBody(searchParams.get("composeBody") ?? "");
    const name = searchParams.get("composeName");
    setSubject(name ? `Follow up — ${name}` : "Follow up");
    setComposeOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("composeTo");
    next.delete("composeBody");
    next.delete("composeName");
    next.delete("leadId");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot deep link from Tasks
  }, []);

  const mailboxQuery = useQuery({
    queryKey: ["mailboxes"],
    queryFn: listMailboxes,
    staleTime: 30_000,
  });
  const sequencesQuery = useQuery({
    queryKey: ["sequences"],
    queryFn: () => listSequences(),
    staleTime: 60_000,
  });
  const listsQuery = useQuery({
    queryKey: ["lists", "people"],
    queryFn: () => listWorkspaceLists({ modality: "people" }),
    staleTime: 60_000,
  });
  const templatesQuery = useQuery({
    queryKey: ["email-templates"],
    queryFn: listEmailTemplates,
    staleTime: 60_000,
  });
  const snippetsQuery = useQuery({
    queryKey: ["email-snippets"],
    queryFn: listEmailSnippets,
    staleTime: 60_000,
  });
  const statsQuery = useQuery({
    queryKey: ["emails", "stats"],
    queryFn: getEmailStats,
    staleTime: 15_000,
  });

  const listQuery = useQuery({
    queryKey: [
      "emails",
      "list",
      status,
      debouncedQ,
      mailboxId,
      fromEmail,
      sequenceId,
      listId,
      dateFrom,
      dateTo,
      notSentReason,
      opened,
      minOpens,
    ],
    queryFn: () =>
      listEmails({
        status: status || undefined,
        q: debouncedQ || undefined,
        mailboxId: mailboxId || undefined,
        fromEmail: fromEmail.trim() || undefined,
        sequenceId: sequenceId || undefined,
        listId: listId || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo ? `${dateTo}T23:59:59.999Z` : undefined,
        notSentReason: notSentReason.trim() || undefined,
        opened: opened === "" ? undefined : opened === "true",
        minOpens: minOpens ? Number(minOpens) : undefined,
        limit: 200,
      }),
  });

  const threadQuery = useQuery({
    queryKey: ["emails", "thread", selectedId],
    queryFn: () => getEmailThread(selectedId!),
    enabled: Boolean(selectedId),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["emails"] });

  const syncMutation = useMutation({
    mutationFn: () => syncEmails(),
    onSuccess: (res) => {
      setMessage(`Synced ${res.created} message(s).`);
      invalidate();
    },
    onError: (err: unknown) =>
      setMessage(err instanceof Error ? err.message : "Sync failed"),
  });

  const processMutation = useMutation({
    mutationFn: () => processScheduledEmails(),
    onSuccess: (res) => {
      setMessage(`Processed: ${res.sent} sent, ${res.failed} failed.`);
      invalidate();
    },
  });

  const composeMutation = useMutation({
    mutationFn: async (mode: "send" | "schedule" | "draft") => {
      const recipients = to
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const ccList = cc
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const payload = {
        to: recipients,
        cc: ccList.length ? ccList : undefined,
        mailboxId: composeMailboxId || undefined,
        subject,
        bodyText: body,
        bodyHtml: body.includes("<") ? body : `<p>${body.replace(/\n/g, "<br/>")}</p>`,
        scheduledAt:
          mode === "schedule" && scheduleAt
            ? new Date(scheduleAt).toISOString()
            : undefined,
        status: mode === "draft" ? ("draft" as const) : undefined,
      };
      if (editingId) {
        const { email } = await updateEmailDraft(editingId, payload);
        if (mode === "send") {
          const sent = await sendEmail(email.emailMessageId);
          return sent.email;
        }
        return email;
      }
      const { email } = await createEmailDraft(payload);
      if (mode === "send") {
        const sent = await sendEmail(email.emailMessageId);
        return sent.email;
      }
      return email;
    },
    onSuccess: (email) => {
      setComposeOpen(false);
      setEditingId(null);
      setTo("");
      setCc("");
      setSubject("");
      setBody("");
      setScheduleAt("");
      setTemplateId("");
      setMessage(
        email.status === "scheduled"
          ? "Email scheduled."
          : email.status === "sent"
            ? "Email sent."
            : email.status === "draft"
              ? "Draft saved."
              : `Status: ${email.status}`,
      );
      invalidate();
      setSelectedId(email.emailMessageId);
    },
    onError: (err: unknown) =>
      setMessage(err instanceof Error ? err.message : "Compose failed"),
  });

  const sendExistingMutation = useMutation({
    mutationFn: (id: string) => sendEmail(id),
    onSuccess: () => {
      setMessage("Email sent.");
      invalidate();
      if (selectedId)
        qc.invalidateQueries({ queryKey: ["emails", "thread", selectedId] });
    },
    onError: (err: unknown) =>
      setMessage(err instanceof Error ? err.message : "Send failed"),
  });

  const replyMutation = useMutation({
    mutationFn: () =>
      replyEmail(selectedId!, {
        bodyText: replyBody,
        bodyHtml: `<p>${replyBody.replace(/\n/g, "<br/>")}</p>`,
        scheduledAt: replyScheduleAt
          ? new Date(replyScheduleAt).toISOString()
          : undefined,
      }),
    onSuccess: (res) => {
      setReplyBody("");
      setReplyScheduleAt("");
      setMessage(
        res.email.status === "scheduled" ? "Reply scheduled." : "Reply sent.",
      );
      invalidate();
      qc.invalidateQueries({ queryKey: ["emails", "thread", selectedId] });
    },
    onError: (err: unknown) =>
      setMessage(err instanceof Error ? err.message : "Reply failed"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteEmail(id),
    onSuccess: () => {
      setMessage("Email deleted.");
      setSelectedId(null);
      invalidate();
    },
  });

  const bulkRetryMut = useMutation({
    mutationFn: () => bulkRetryEmails({ emailMessageIds: selectedIds }),
    onSuccess: (r) => {
      setMessage(`Retry: ${r.sent} sent, ${r.failed} failed.`);
      setSelectedIds([]);
      invalidate();
    },
  });

  const bulkDeleteMut = useMutation({
    mutationFn: () => bulkDeleteEmails({ emailMessageIds: selectedIds }),
    onSuccess: (r) => {
      setMessage(`Deleted ${r.deleted}.`);
      setSelectedIds([]);
      invalidate();
    },
  });

  const bulkRescheduleMut = useMutation({
    mutationFn: (hours: number) => {
      const at = new Date(Date.now() + hours * 3600_000).toISOString();
      return bulkRescheduleEmails({
        emailMessageIds: selectedIds,
        scheduledAt: at,
      });
    },
    onSuccess: (r) => {
      setMessage(`Rescheduled ${r.updated}.`);
      setSelectedIds([]);
      invalidate();
    },
  });

  const emails = listQuery.data?.emails ?? [];
  const stats = statsQuery.data;
  const needsReconsent = mailboxQuery.data?.needsReconsent;
  const hasMailbox = (mailboxQuery.data?.mailboxes.length ?? 0) > 0;
  const selected = threadQuery.data?.message;
  const thread = threadQuery.data?.thread ?? [];
  const templates = templatesQuery.data?.templates ?? [];
  const snippets = snippetsQuery.data?.snippets ?? [];

  // Clear thread selection when the filtered list no longer contains it
  // (status/q change or send/delete moved the row out of view).
  useEffect(() => {
    if (!selectedId || listQuery.isFetching || listQuery.isLoading) return;
    if (emails.some((e) => e.emailMessageId === selectedId)) return;
    setSelectedId(null);
  }, [emails, selectedId, listQuery.isFetching, listQuery.isLoading]);

  useEffect(() => {
    const ids = new Set(emails.map((e) => e.emailMessageId));
    setSelectedIds((prev) => {
      const next = prev.filter((id) => ids.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [emails]);

  function applyTemplate(t: EmailTemplate) {
    setTemplateId(t.templateId);
    setSubject(t.subject);
    setBody(t.bodyText || stripHtml(t.bodyHtml) || "");
  }

  function openEdit(email: EmailMessage) {
    setEditingId(email.emailMessageId);
    setTo(email.to.join(", "));
    setCc((email.cc ?? []).join(", "));
    setSubject(email.subject);
    setBody(email.bodyText || stripHtml(email.bodyHtml));
    setComposeMailboxId(email.mailboxId);
    setScheduleAt(
      email.scheduledAt
        ? new Date(email.scheduledAt).toISOString().slice(0, 16)
        : "",
    );
    setComposeOpen(true);
    setRowMenuId(null);
  }

  function exportCsv() {
    const rows = emails.filter(
      (e) => selectedIds.length === 0 || selectedIds.includes(e.emailMessageId),
    );
    const header = [
      "status",
      "subject",
      "from",
      "to",
      "opens",
      "clicks",
      "sentAt",
      "notSentReason",
    ];
    const lines = [
      header.join(","),
      ...rows.map((e) =>
        [
          e.status,
          JSON.stringify(e.subject),
          JSON.stringify(e.from),
          JSON.stringify(e.to.join(";")),
          e.openCount ?? 0,
          e.clickCount ?? 0,
          e.sentAt ?? "",
          JSON.stringify(e.notSentReason ?? e.lastError ?? ""),
        ].join(","),
      ),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "emails-export.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  const emptyHint = useMemo(() => {
    if (!hasMailbox || needsReconsent) {
      return (
        <span>
          Connect a mailbox in{" "}
          <Link
            to="/settings/account/mailboxes"
            className="text-brand hover:underline"
          >
            Settings → Mailboxes
          </Link>
          .
        </span>
      );
    }
    return "No emails match these filters. Compose or sync.";
  }, [hasMailbox, needsReconsent]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
            className="rounded-md border border-border px-3 py-1.5 text-sm"
          >
            Sync
          </button>
          <button
            type="button"
            onClick={() => processMutation.mutate()}
            disabled={processMutation.isPending}
            className="rounded-md border border-border px-3 py-1.5 text-sm"
          >
            Send scheduled
          </button>
          <button
            type="button"
            onClick={() => {
              setEditingId(null);
              setComposeOpen(true);
            }}
            className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-white"
          >
            Compose
          </button>
        </div>
        <button
          type="button"
          className="text-sm text-brand underline"
          onClick={() => setShowFilters((v) => !v)}
        >
          {showFilters ? "Hide filters" : "Show filters"}
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {(
          [
            ["Scheduled", stats?.scheduled],
            ["Delivered", stats?.sent],
            ["Opened", stats?.opened],
            ["Clicked", stats?.clicked],
            ["Replied", stats?.replied],
            ["Failed", stats?.failed],
          ] as const
        ).map(([label, value]) => (
          <div
            key={label}
            className="rounded-lg border border-border bg-surface/40 px-3 py-2"
          >
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-lg font-semibold tabular-nums">{value ?? "—"}</p>
          </div>
        ))}
      </div>

      {message && (
        <p className="text-sm text-muted-foreground">{message}</p>
      )}

      <div className="flex flex-wrap gap-1.5">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.label}
            type="button"
            onClick={() => setStatus(tab.id)}
            className={cn(
              "rounded-md px-2.5 py-1 text-xs font-medium",
              status === tab.id
                ? "bg-brand text-white"
                : "border border-border text-muted-foreground hover:bg-surface-hover",
            )}
          >
            {tab.label}
          </button>
        ))}
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search subject or address…"
          className="min-w-[180px] flex-1 rounded-md border border-border bg-background px-2 py-1 text-sm"
        />
      </div>

      {showFilters && (
        <div className="grid gap-2 rounded-lg border border-border bg-surface/30 p-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="text-xs">
            From mailbox
            <select
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={mailboxId}
              onChange={(e) => setMailboxId(e.target.value)}
            >
              <option value="">Any</option>
              {(mailboxQuery.data?.mailboxes ?? []).map((m) => (
                <option key={m.mailboxId} value={m.mailboxId}>
                  {m.emailAddress}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs">
            From email
            <input
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              placeholder="user@domain.com"
            />
          </label>
          <label className="text-xs">
            Sequence
            <select
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={sequenceId}
              onChange={(e) => setSequenceId(e.target.value)}
            >
              <option value="">Any</option>
              {(sequencesQuery.data?.sequences ?? []).map((s) => (
                <option key={s.sequenceId} value={s.sequenceId}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs">
            Contact list
            <select
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={listId}
              onChange={(e) => setListId(e.target.value)}
            >
              <option value="">Any</option>
              {(listsQuery.data?.lists ?? []).map((l) => (
                <option key={l.listId} value={l.listId}>
                  {l.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs">
            Date from
            <input
              type="date"
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </label>
          <label className="text-xs">
            Date to
            <input
              type="date"
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </label>
          <label className="text-xs">
            Not sent reason
            <input
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={notSentReason}
              onChange={(e) => setNotSentReason(e.target.value)}
              placeholder="missing variable, limit…"
            />
          </label>
          <label className="text-xs">
            Email opened
            <select
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={opened}
              onChange={(e) =>
                setOpened(e.target.value as "" | "true" | "false")
              }
            >
              <option value="">Any</option>
              <option value="true">Opened</option>
              <option value="false">Not opened</option>
            </select>
          </label>
          <label className="text-xs">
            Min opens
            <input
              type="number"
              min={0}
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={minOpens}
              onChange={(e) => setMinOpens(e.target.value)}
            />
          </label>
        </div>
      )}

      {selectedIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface/50 px-3 py-2 text-sm">
          <span className="text-muted-foreground">
            {selectedIds.length} selected
          </span>
          <button
            type="button"
            className="rounded border border-border px-2 py-0.5 text-xs"
            onClick={() => bulkRescheduleMut.mutate(1)}
          >
            Reschedule +1h
          </button>
          <button
            type="button"
            className="rounded border border-border px-2 py-0.5 text-xs"
            onClick={() => bulkRescheduleMut.mutate(24)}
          >
            +24h
          </button>
          <button
            type="button"
            className="rounded border border-border px-2 py-0.5 text-xs"
            onClick={() => bulkRetryMut.mutate()}
          >
            Retry
          </button>
          <button
            type="button"
            className="rounded border border-border px-2 py-0.5 text-xs text-red-600"
            onClick={() => bulkDeleteMut.mutate()}
          >
            Delete
          </button>
          <button
            type="button"
            className="rounded border border-border px-2 py-0.5 text-xs"
            onClick={exportCsv}
          >
            Export CSV
          </button>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
        <div className="min-w-0 overflow-x-auto overscroll-x-contain rounded-lg border border-border [-webkit-overflow-scrolling:touch]">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border bg-surface/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="w-8 px-2 py-2">
                  <input
                    type="checkbox"
                    checked={
                      emails.length > 0 &&
                      selectedIds.length === emails.length
                    }
                    onChange={(e) =>
                      setSelectedIds(
                        e.target.checked
                          ? emails.map((x) => x.emailMessageId)
                          : [],
                      )
                    }
                  />
                </th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Subject</th>
                <th className="px-3 py-2">To / From</th>
                <th className="px-3 py-2">Opens</th>
                <th className="px-3 py-2">Clicks</th>
                <th className="px-3 py-2">When</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {listQuery.isLoading && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-3 py-8 text-center text-muted-foreground"
                  >
                    Loading…
                  </td>
                </tr>
              )}
              {!listQuery.isLoading && emails.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-3 py-8 text-center text-muted-foreground"
                  >
                    {emptyHint}
                  </td>
                </tr>
              )}
              {emails.map((email) => (
                <tr
                  key={email.emailMessageId}
                  className={cn(
                    "border-b border-border/60 hover:bg-surface-hover/40",
                    selectedId === email.emailMessageId && "bg-brand/5",
                  )}
                >
                  <td className="px-2 py-2">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(email.emailMessageId)}
                      onChange={(e) =>
                        setSelectedIds((prev) =>
                          e.target.checked
                            ? [...prev, email.emailMessageId]
                            : prev.filter((id) => id !== email.emailMessageId),
                        )
                      }
                    />
                  </td>
                  <td
                    className="cursor-pointer px-3 py-2"
                    onClick={() => setSelectedId(email.emailMessageId)}
                    title={
                      email.notSentReason || email.lastError
                        ? `Not sent: ${email.notSentReason || email.lastError}`
                        : statusLabel(email.status)
                    }
                  >
                    <span className="rounded bg-surface px-1.5 py-0.5 text-xs capitalize">
                      {statusLabel(email.status)}
                    </span>
                  </td>
                  <td
                    className="cursor-pointer px-3 py-2 font-medium"
                    onClick={() => setSelectedId(email.emailMessageId)}
                  >
                    {email.subject}
                    {email.sequenceId && (
                      <span className="ml-1 text-xs font-normal text-muted-foreground">
                        · seq
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {email.direction === "inbound"
                      ? email.from
                      : email.to.join(", ")}
                  </td>
                  <td className="px-3 py-2 tabular-nums">
                    {(email.openCount ?? 0) > 0 ? (
                      <span className="text-brand" title={email.openedAt}>
                        {email.openCount}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-3 py-2 tabular-nums">
                    {(email.clickCount ?? 0) > 0 ? (
                      <span className="text-brand" title={email.clickedAt}>
                        {email.clickCount}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {formatWhen(
                      email.sentAt ||
                        email.scheduledAt ||
                        email.repliedAt ||
                        email.updatedAt,
                    )}
                  </td>
                  <td className="relative px-2 py-2">
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground"
                      onClick={() =>
                        setRowMenuId((id) =>
                          id === email.emailMessageId
                            ? null
                            : email.emailMessageId,
                        )
                      }
                    >
                      ···
                    </button>
                    {rowMenuId === email.emailMessageId && (
                      <div className="absolute right-2 z-20 mt-1 w-48 rounded-md border border-border bg-card py-1 shadow-lg">
                        {(email.status === "draft" ||
                          email.status === "scheduled" ||
                          email.status === "failed") && (
                          <button
                            type="button"
                            className="block w-full px-3 py-1.5 text-left text-xs hover:bg-surface-hover"
                            onClick={() => openEdit(email)}
                          >
                            Edit
                          </button>
                        )}
                        {(email.status === "draft" ||
                          email.status === "scheduled" ||
                          email.status === "failed" ||
                          email.status === "bounced") && (
                          <>
                            <button
                              type="button"
                              className="block w-full px-3 py-1.5 text-left text-xs hover:bg-surface-hover"
                              onClick={() =>
                                sendExistingMutation.mutate(
                                  email.emailMessageId,
                                )
                              }
                            >
                              {email.status === "failed" ||
                              email.status === "bounced"
                                ? "Retry"
                                : "Send now"}
                            </button>
                            <button
                              type="button"
                              className="block w-full px-3 py-1.5 text-left text-xs text-red-600 hover:bg-surface-hover"
                              onClick={() =>
                                deleteMutation.mutate(email.emailMessageId)
                              }
                            >
                              Delete
                            </button>
                          </>
                        )}
                        <button
                          type="button"
                          className="block w-full px-3 py-1.5 text-left text-xs hover:bg-surface-hover"
                          onClick={() => {
                            setSelectedId(email.emailMessageId);
                            setDiagnosticsOpen(true);
                            setRowMenuId(null);
                          }}
                        >
                          Show diagnostics
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <aside className="rounded-lg border border-border p-3">
          {!selectedId ? (
            <p className="text-sm text-muted-foreground">
              Select an email to view the thread, reply, or inspect diagnostics.
            </p>
          ) : threadQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading thread…</p>
          ) : (
            <>
              <div className="mb-3 flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold">{selected?.subject}</p>
                  <p className="text-xs capitalize text-muted-foreground">
                    {selected ? statusLabel(selected.status) : ""} ·{" "}
                    {selected?.direction}
                    {(selected?.openCount ?? 0) > 0 &&
                      ` · ${selected?.openCount} opens`}
                    {(selected?.clickCount ?? 0) > 0 &&
                      ` · ${selected?.clickCount} clicks`}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {selected &&
                    (selected.status === "draft" ||
                      selected.status === "failed" ||
                      selected.status === "scheduled") && (
                      <button
                        type="button"
                        className="text-xs text-brand underline"
                        onClick={() =>
                          sendExistingMutation.mutate(selected.emailMessageId)
                        }
                      >
                        Send now
                      </button>
                    )}
                  <button
                    type="button"
                    className="text-xs text-muted-foreground underline"
                    onClick={() => setDiagnosticsOpen((v) => !v)}
                  >
                    Diagnostics
                  </button>
                </div>
              </div>

              {diagnosticsOpen && selected && (
                <div className="mb-3 space-y-1 rounded-md bg-surface/60 p-2 text-xs text-muted-foreground">
                  <p>Mailbox: {selected.mailboxId}</p>
                  <p>Scheduled: {formatWhen(selected.scheduledAt)}</p>
                  <p>Sent: {formatWhen(selected.sentAt)}</p>
                  <p>Opened: {formatWhen(selected.openedAt)} ({selected.openCount ?? 0})</p>
                  <p>Clicked: {formatWhen(selected.clickedAt)} ({selected.clickCount ?? 0})</p>
                  {(selected.notSentReason || selected.lastError) && (
                    <p className="text-red-600">
                      {selected.notSentReason || selected.lastError}
                    </p>
                  )}
                  {selected.sequenceId && (
                    <p>
                      Sequence:{" "}
                      <Link
                        to={`/sequences/${selected.sequenceId}`}
                        className="text-brand underline"
                      >
                        open
                      </Link>
                    </p>
                  )}
                </div>
              )}

              <ul className="mb-3 max-h-72 space-y-2 overflow-y-auto">
                {thread.map((m) => (
                  <li
                    key={m.emailMessageId}
                    className="rounded-md border border-border/70 px-2 py-2 text-sm"
                  >
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>
                        {m.direction === "inbound"
                          ? m.from
                          : `to ${m.to.join(", ")}`}
                      </span>
                      <span>{formatWhen(m.sentAt || m.createdAt)}</span>
                    </div>
                    <p className="mt-1 whitespace-pre-wrap">
                      {m.bodyText || m.snippet || stripHtml(m.bodyHtml)}
                    </p>
                  </li>
                ))}
              </ul>

              {selected &&
                (selected.status === "sent" ||
                  selected.status === "replied") && (
                  <div className="space-y-2">
                    <textarea
                      className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                      rows={3}
                      placeholder="Reply…"
                      value={replyBody}
                      onChange={(e) => setReplyBody(e.target.value)}
                    />
                    {snippets.length > 0 && (
                      <select
                        className="w-full rounded-md border border-border bg-background px-2 py-1 text-xs"
                        defaultValue=""
                        onChange={(e) => {
                          const sn = snippets.find(
                            (s) => s.snippetId === e.target.value,
                          );
                          if (sn) setReplyBody((prev) => prev + sn.bodyText);
                        }}
                      >
                        <option value="">Insert snippet…</option>
                        {snippets.map((s) => (
                          <option key={s.snippetId} value={s.snippetId}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                    )}
                    <label className="block text-xs text-muted-foreground">
                      Schedule reply (optional)
                      <input
                        type="datetime-local"
                        className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
                        value={replyScheduleAt}
                        onChange={(e) => setReplyScheduleAt(e.target.value)}
                      />
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={!replyBody.trim() || replyMutation.isPending}
                        onClick={() => {
                          setReplyScheduleAt("");
                          replyMutation.mutate();
                        }}
                        className="rounded-md bg-brand px-3 py-1.5 text-sm text-white disabled:opacity-50"
                      >
                        Reply now
                      </button>
                      <button
                        type="button"
                        disabled={
                          !replyBody.trim() ||
                          !replyScheduleAt ||
                          replyMutation.isPending
                        }
                        onClick={() => replyMutation.mutate()}
                        className="rounded-md border border-border px-3 py-1.5 text-sm disabled:opacity-50"
                      >
                        Schedule
                      </button>
                    </div>
                  </div>
                )}
            </>
          )}
        </aside>
      </div>

      {composeOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setComposeOpen(false)}
        >
          <div
            className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-lg border border-border bg-card p-4 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="mb-3 text-sm font-semibold">
              {editingId ? "Edit email" : "Compose email"}
            </h2>
            {(mailboxQuery.data?.mailboxes.length ?? 0) > 0 && (
              <label className="mb-2 block text-sm">
                From mailbox
                <select
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                  value={composeMailboxId}
                  onChange={(e) => setComposeMailboxId(e.target.value)}
                >
                  <option value="">Primary</option>
                  {mailboxQuery.data!.mailboxes.map((m) => (
                    <option key={m.mailboxId} value={m.mailboxId}>
                      {m.emailAddress} ({m.provider}
                      {m.isPrimary ? ", primary" : ""})
                    </option>
                  ))}
                </select>
              </label>
            )}
            {templates.length > 0 && (
              <label className="mb-2 block text-sm">
                Load template
                <select
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                  value={templateId}
                  onChange={(e) => {
                    const t = templates.find(
                      (x) => x.templateId === e.target.value,
                    );
                    if (t) applyTemplate(t);
                  }}
                >
                  <option value="">None</option>
                  {templates.map((t) => (
                    <option key={t.templateId} value={t.templateId}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="mb-2 block text-sm">
              To
              <input
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="prospect@company.com"
              />
            </label>
            <label className="mb-2 block text-sm">
              Cc
              <input
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder="optional@company.com"
              />
            </label>
            <label className="mb-2 block text-sm">
              Subject
              <input
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </label>
            <label className="mb-2 block text-sm">
              Body
              <textarea
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                rows={8}
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
            </label>
            {snippets.length > 0 && (
              <select
                className="mb-2 w-full rounded-md border border-border bg-background px-2 py-1 text-xs"
                defaultValue=""
                onChange={(e) => {
                  const sn = snippets.find(
                    (s) => s.snippetId === e.target.value,
                  );
                  if (sn) setBody((prev) => prev + sn.bodyText);
                }}
              >
                <option value="">Insert snippet…</option>
                {snippets.map((s) => (
                  <option key={s.snippetId} value={s.snippetId}>
                    {s.name}
                  </option>
                ))}
              </select>
            )}
            <label className="mb-3 block text-sm">
              Schedule
              <input
                type="datetime-local"
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                value={scheduleAt}
                onChange={(e) => setScheduleAt(e.target.value)}
              />
            </label>
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                className="text-sm text-muted-foreground"
                onClick={() => setComposeOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!to.trim() || !subject.trim()}
                onClick={() => composeMutation.mutate("draft")}
                className="rounded-md border border-border px-3 py-1.5 text-sm"
              >
                Save draft
              </button>
              <button
                type="button"
                disabled={!to.trim() || !subject.trim() || !scheduleAt}
                onClick={() => composeMutation.mutate("schedule")}
                className="rounded-md border border-border px-3 py-1.5 text-sm"
              >
                Schedule
              </button>
              <button
                type="button"
                disabled={
                  !to.trim() || !subject.trim() || composeMutation.isPending
                }
                onClick={() => composeMutation.mutate("send")}
                className="rounded-md bg-brand px-3 py-1.5 text-sm text-white disabled:opacity-50"
              >
                Send now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function EmailsTemplatesPage() {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ["email-templates"],
    queryFn: listEmailTemplates,
  });

  const saveMut = useMutation({
    mutationFn: async () => {
      const { createEmailTemplate, updateEmailTemplate } = await import(
        "@/api/email-templates"
      );
      if (editingId) {
        return updateEmailTemplate(editingId, {
          name,
          subject,
          bodyText: body,
          bodyHtml: `<p>${body.replace(/\n/g, "<br/>")}</p>`,
        });
      }
      return createEmailTemplate({
        name,
        subject,
        bodyText: body,
        bodyHtml: `<p>${body.replace(/\n/g, "<br/>")}</p>`,
      });
    },
    onSuccess: () => {
      setName("");
      setSubject("");
      setBody("");
      setEditingId(null);
      setMessage("Template saved.");
      qc.invalidateQueries({ queryKey: ["email-templates"] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const { deleteEmailTemplate } = await import("@/api/email-templates");
      return deleteEmailTemplate(id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["email-templates"] }),
  });

  const templates = listQuery.data?.templates ?? [];

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Create reusable templates for one-off sends and sequence steps (Apollo
          Emails → Templates).
        </p>
        {message && (
          <p className="text-sm text-muted-foreground">{message}</p>
        )}
        <div className="min-w-0 overflow-x-auto overscroll-x-contain rounded-lg border border-border [-webkit-overflow-scrolling:touch]">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead className="border-b border-border bg-surface/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Subject</th>
                <th className="px-3 py-2">Updated</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {templates.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-3 py-6 text-muted-foreground"
                  >
                    No templates yet.
                  </td>
                </tr>
              )}
              {templates.map((t) => (
                <tr key={t.templateId} className="border-b border-border/60">
                  <td className="px-3 py-2 font-medium">{t.name}</td>
                  <td className="px-3 py-2">{t.subject}</td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {formatWhen(t.updatedAt)}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      className="mr-2 text-xs text-brand underline"
                      onClick={() => {
                        setEditingId(t.templateId);
                        setName(t.name);
                        setSubject(t.subject);
                        setBody(t.bodyText || stripHtml(t.bodyHtml));
                      }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="text-xs text-red-600 underline"
                      onClick={() => deleteMut.mutate(t.templateId)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="rounded-lg border border-border p-4">
        <h2 className="mb-3 text-sm font-semibold">
          {editingId ? "Edit template" : "New template"}
        </h2>
        <label className="mb-2 block text-sm">
          Name
          <input
            className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className="mb-2 block text-sm">
          Subject
          <input
            className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </label>
        <label className="mb-3 block text-sm">
          Body
          <textarea
            className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
            rows={8}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </label>
        <button
          type="button"
          disabled={!name.trim() || !subject.trim()}
          className="rounded-md bg-brand px-3 py-1.5 text-sm text-white disabled:opacity-50"
          onClick={() => saveMut.mutate()}
        >
          Save template
        </button>
      </div>
    </div>
  );
}
